import { EventCard } from '../event-card';
import { excludeHeroDeckGroups, HERO_DECK_BREAKPOINT } from '../hero-deck';
import { MarketCard, MarketCardSkeleton } from '../market-card';
import { groupMarketsByEvent } from '../market-list/event-groups';
import { TrendingSectionEntry } from './section-entry';
import { selectTrendingSectionEventIds } from './select-trending-section';
import { useMediaQuery } from '@/client/hooks/use-media-query';
import { useTrendingMarkets } from '@/client/hooks/use-trending-markets';
import { useAppSettingStore } from '@/client/store/app-store';
import { api } from '@/client/utils/api';
import { useSections } from '@/libs/react-sdk/api/use-sections';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';

/**
 * "Trending now" section (design handoff) — a 3-up grid above the browse
 * list. Prefers admin-curated pins (get-sections); falls back to the
 * volume-sorted markets endpoint when nothing is pinned for the current lens
 * (e.g. a cold-start collateral with no curation yet).
 */
export const TrendingNow = () => {
  const { t } = useTranslation(['home']);
  const [, navigate] = useLocation();
  const baseTokenAddress = useAppSettingStore(state => state.baseToken.address);

  const { data: sections, isLoading: isSectionsLoading } = useSections(api, { baseTokenAddress });
  const sectionEventIds = useMemo(() => selectTrendingSectionEventIds(sections), [sections]);
  const useCuratedRail = sectionEventIds.length > 0;

  const { data, isLoading: isMarketsLoading } = useTrendingMarkets();
  // Desktop renders the hero deck above this rail from the same query, so the
  // fallback skips the deck's cards (QA ISSUE-001: identical rails back to
  // back). On mobile the deck is not rendered — the rail keeps the top slots.
  const isMobile = useMediaQuery(`(max-width: ${HERO_DECK_BREAKPOINT}px)`);
  // Collapse multi-market events into one card, then take the top 3 items so a
  // lone event leg (e.g. one WC team) never surfaces as a standalone trending card.
  const top = useMemo(() => {
    const markets = data?.pages.flatMap(p => p.markets) ?? [];
    const groups = groupMarketsByEvent(markets);
    return (isMobile ? groups : excludeHeroDeckGroups(groups, markets)).slice(0, 3);
  }, [data, isMobile]);

  // Fallback branch only needs the markets query, so it's the only branch
  // that waits on isMarketsLoading -- the curated branch renders as soon as
  // sections resolve.
  const isLoading = isSectionsLoading || (!useCuratedRail && isMarketsLoading);

  if (!isLoading && !useCuratedRail && top.length === 0) return null;

  return (
    <section className="px-4 md:px-6 pt-6 pb-2">
      <div className="neon-sechead">
        <div>
          <h2>{t('trending-now', 'Trending now')}</h2>
          <p>{t('trending-now-sub', 'Markets moving fast in the last 24 hours.')}</p>
        </div>
        <span className="neon-viewall" onClick={() => navigate('/markets')}>
          {t('view-all', 'View all')} →
        </span>
      </div>
      <div className="neon-grid-3">
        {isLoading && Array.from({ length: 3 }).map((_, i) => <MarketCardSkeleton key={i} />)}
        {!isLoading &&
          useCuratedRail &&
          sectionEventIds.map((eventId, i) => <TrendingSectionEntry key={eventId} eventId={eventId} index={i} />)}
        {!isLoading &&
          !useCuratedRail &&
          top.map((g, i) =>
            g.markets.length > 1 ? (
              <EventCard key={`ev-${g.eventId}`} eventId={g.eventId} markets={g.markets} />
            ) : (
              <MarketCard key={g.markets[0].marketAddress} market={g.markets[0]} index={i} />
            )
          )}
      </div>
    </section>
  );
};
